import React, { useState, useCallback, useMemo } from "react";
import {
    Box,
    Typography,
    Button,
    Alert,
    Card,
    CardContent,
    CardHeader,
    Chip,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    CircularProgress,
    Tooltip,
    LinearProgress,
    Tabs,
    Tab,
} from "@mui/material";
import {
    MergeType,
    SwapHoriz,
    CheckCircle,
    Warning,
} from "@mui/icons-material";
import type { Product } from "../../types";
import { ProductSearchAutocomplete } from "../../components/common/ProductSearchAutocomplete";
import {
    productMergeService,
    MergeCandidate,
    MergeableField,
} from "../../services/productMergeService";
import { formatCurrencyBDT } from "../../utils/currency";

// ── Field metadata ─────────────────────────────────────────────────────────

interface FieldMeta {
    key: MergeableField;
    label: string;
    render: (p: Product) => React.ReactNode;
}

const FIELD_META: FieldMeta[] = [
    { key: "name", label: "Name", render: (p) => p.name },
    {
        key: "description",
        label: "Description",
        render: (p) => p.description || <em style={{ opacity: 0.4 }}>—</em>,
    },
    { key: "sku", label: "SKU", render: (p) => p.sku },
    {
        key: "barcode",
        label: "Barcode",
        render: (p) => p.barcode || <em style={{ opacity: 0.4 }}>—</em>,
    },
    {
        key: "selling_price",
        label: "Selling Price",
        render: (p) => formatCurrencyBDT(Number(p.selling_price)),
    },
    {
        key: "cost_price",
        label: "Cost Price",
        render: (p) => formatCurrencyBDT(Number(p.cost_price)),
    },
    {
        key: "weight",
        label: "Net Weight",
        render: (p) =>
            p.weight != null ? `${p.weight} kg` : <em style={{ opacity: 0.4 }}>—</em>,
    },
    {
        key: "gross_weight",
        label: "Gross Weight",
        render: (p) =>
            p.gross_weight != null ? `${p.gross_weight} kg` : <em style={{ opacity: 0.4 }}>—</em>,
    },
    {
        key: "dimensions",
        label: "Dimensions",
        render: (p) =>
            p.dimensions || <em style={{ opacity: 0.4 }}>—</em>,
    },
    {
        key: "is_featured",
        label: "Featured",
        render: (p) =>
            p.is_featured ? (
                <Chip label="Featured" color="secondary" size="small" />
            ) : (
                <Chip label="No" size="small" variant="outlined" />
            ),
    },
    {
        key: "is_visible",
        label: "Visible on Website",
        render: (p) =>
            p.is_visible ? (
                <Chip label="Visible" color="primary" size="small" />
            ) : (
                <Chip label="Hidden" color="warning" size="small" variant="outlined" />
            ),
    },
];

// ── Component ──────────────────────────────────────────────────────────────

export const ProductMerge: React.FC = () => {
    const [tab, setTab] = useState(0);

    // Manual merge: the two products chosen by the admin
    const [productA, setProductA] = useState<Product | null>(null);
    const [productB, setProductB] = useState<Product | null>(null);

    // Which product is the master (surviving) — "A" or "B"
    const [masterSide, setMasterSide] = useState<"A" | "B">("A");

    // Per-field override: "A" = keep A's value, "B" = keep B's value
    // Default: follow masterSide for every field
    const [fieldSides, setFieldSides] = useState<
        Partial<Record<MergeableField, "A" | "B">>
    >({});

    const [mergeReason, setMergeReason] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [merging, setMerging] = useState(false);
    const [mergeError, setMergeError] = useState("");
    const [mergeSuccess, setMergeSuccess] = useState("");

    // Candidates tab
    const [candidates, setCandidates] = useState<MergeCandidate[]>([]);
    const [loadingCandidates, setLoadingCandidates] = useState(false);
    const [candidateError, setCandidateError] = useState("");
    const [selectedCandidate, setSelectedCandidate] =
        useState<MergeCandidate | null>(null);

    // ── Derived state ──────────────────────────────────────────────────────

    const master = masterSide === "A" ? productA : productB;
    const duplicate = masterSide === "A" ? productB : productA;

    // Build the field_overrides payload: only emit overrides where the admin
    // chose the non-master side.
    const fieldOverrides = useMemo(() => {
        const overrides: Partial<Record<MergeableField, "master" | "duplicate">> =
            {};
        for (const meta of FIELD_META) {
            const side = fieldSides[meta.key] ?? masterSide;
            const isNonMaster = side !== masterSide;
            if (isNonMaster) {
                overrides[meta.key] = "duplicate";
            }
        }
        return overrides;
    }, [fieldSides, masterSide]);

    // ── Handlers ──────────────────────────────────────────────────────────

    const handleSwapMaster = useCallback(() => {
        setMasterSide((s) => (s === "A" ? "B" : "A"));
        setFieldSides({}); // reset per-field overrides on master swap
    }, []);

    const toggleFieldSide = useCallback(
        (field: MergeableField) => {
            setFieldSides((prev) => {
                const current = prev[field] ?? masterSide;
                const next = current === "A" ? "B" : "A";
                return { ...prev, [field]: next };
            });
        },
        [masterSide],
    );

    const handleLoadCandidates = useCallback(async () => {
        setLoadingCandidates(true);
        setCandidateError("");
        try {
            const data = await productMergeService.getCandidates(
                undefined,
                0.6,
                50,
            );
            setCandidates(data);
        } catch {
            setCandidateError("Failed to load duplicate candidates");
        } finally {
            setLoadingCandidates(false);
        }
    }, []);

    const handleReviewCandidate = useCallback((c: MergeCandidate) => {
        setSelectedCandidate(c);
        setTab(0); // switch to "Merge Products" tab
        // We don't pre-fill the autocompletes here — the admin will confirm
        // which is master after reviewing the side-by-side.
    }, []);

    const handleMerge = useCallback(async () => {
        if (!master || !duplicate) return;
        setMerging(true);
        setMergeError("");
        try {
            const merged = await productMergeService.mergeProducts({
                master_product_id: master.product_id,
                duplicate_product_id: duplicate.product_id,
                merge_reason: mergeReason || undefined,
                field_overrides:
                    Object.keys(fieldOverrides).length > 0
                        ? fieldOverrides
                        : undefined,
            });
            setMergeSuccess(
                `Products successfully merged into "${merged.name}". The duplicate has been deactivated.`,
            );
            // Reset form
            setProductA(null);
            setProductB(null);
            setMasterSide("A");
            setFieldSides({});
            setMergeReason("");
            setSelectedCandidate(null);
        } catch (err: any) {
            setMergeError(
                err?.response?.data?.detail ||
                    err?.message ||
                    "Merge failed. Please try again.",
            );
        } finally {
            setMerging(false);
            setConfirmOpen(false);
        }
    }, [master, duplicate, mergeReason, fieldOverrides]);

    // ── Render ─────────────────────────────────────────────────────────────

    const canMerge =
        productA !== null &&
        productB !== null &&
        productA.product_id !== productB.product_id;

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Merge Products
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Merge a duplicate product into a master product. All order
                    history, purchase records, stock, and images are transferred.
                    The duplicate is deactivated and hidden from the website.
                </Typography>
            </Box>

            {mergeSuccess && (
                <Alert
                    severity="success"
                    sx={{ mb: 2 }}
                    onClose={() => setMergeSuccess("")}
                >
                    {mergeSuccess}
                </Alert>
            )}
            {mergeError && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    onClose={() => setMergeError("")}
                >
                    {mergeError}
                </Alert>
            )}

            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
            >
                <Tab label="Merge Products" />
                <Tab
                    label={
                        candidates.length > 0
                            ? `Duplicate Candidates (${candidates.length})`
                            : "Duplicate Candidates"
                    }
                />
            </Tabs>

            {/* ── Tab 0: Manual merge ─────────────────────────────── */}
            {tab === 0 && (
                <Box>
                    {selectedCandidate && (
                        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setSelectedCandidate(null)}>
                            Reviewing candidate:{" "}
                            <strong>{selectedCandidate.product1_name}</strong>{" "}
                            vs{" "}
                            <strong>{selectedCandidate.product2_name}</strong>{" "}
                            (score:{" "}
                            {Math.round(selectedCandidate.match_score * 100)}%).
                            Search and select both products below.
                        </Alert>
                    )}

                    {/* Product pickers */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "1fr auto 1fr" },
                            gap: 2,
                            alignItems: "flex-start",
                            mb: 3,
                        }}
                    >
                        <Card variant="outlined">
                            <CardHeader
                                title={
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            Product A
                                        </Typography>
                                        {masterSide === "A" && (
                                            <Chip
                                                label="MASTER"
                                                color="success"
                                                size="small"
                                            />
                                        )}
                                        {masterSide === "B" && (
                                            <Chip
                                                label="DUPLICATE"
                                                color="warning"
                                                size="small"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                }
                                sx={{ pb: 0 }}
                            />
                            <CardContent>
                                <ProductSearchAutocomplete
                                    value={productA}
                                    onChange={(p) =>
                                        setProductA(p as Product | null)
                                    }
                                    label="Search product A"
                                />
                            </CardContent>
                        </Card>

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                pt: { xs: 0, md: 4 },
                                gap: 1,
                            }}
                        >
                            <Tooltip title="Swap master / duplicate">
                                <Button
                                    variant="outlined"
                                    onClick={handleSwapMaster}
                                    sx={{ minWidth: "auto", p: 1 }}
                                    disabled={!canMerge}
                                >
                                    <SwapHoriz />
                                </Button>
                            </Tooltip>
                            <Typography variant="caption" color="text.secondary">
                                Swap
                            </Typography>
                        </Box>

                        <Card variant="outlined">
                            <CardHeader
                                title={
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            Product B
                                        </Typography>
                                        {masterSide === "B" && (
                                            <Chip
                                                label="MASTER"
                                                color="success"
                                                size="small"
                                            />
                                        )}
                                        {masterSide === "A" && (
                                            <Chip
                                                label="DUPLICATE"
                                                color="warning"
                                                size="small"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                }
                                sx={{ pb: 0 }}
                            />
                            <CardContent>
                                <ProductSearchAutocomplete
                                    value={productB}
                                    onChange={(p) =>
                                        setProductB(p as Product | null)
                                    }
                                    label="Search product B"
                                />
                            </CardContent>
                        </Card>
                    </Box>

                    {/* Side-by-side field comparison */}
                    {canMerge && (
                        <>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <strong>Click any row</strong> to toggle which
                                product's value will be used in the merged result.
                                The highlighted column is the one that will be
                                kept.
                            </Alert>

                            <TableContainer
                                component={Paper}
                                variant="outlined"
                                sx={{ mb: 3 }}
                            >
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{ width: 130, fontWeight: "bold" }}
                                            >
                                                Field
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: "bold",
                                                    bgcolor:
                                                        masterSide === "A"
                                                            ? "success.50"
                                                            : undefined,
                                                }}
                                            >
                                                A — {productA!.name}
                                                {masterSide === "A" && (
                                                    <Chip
                                                        label="MASTER"
                                                        color="success"
                                                        size="small"
                                                        sx={{ ml: 1 }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: "bold",
                                                    bgcolor:
                                                        masterSide === "B"
                                                            ? "success.50"
                                                            : undefined,
                                                }}
                                            >
                                                B — {productB!.name}
                                                {masterSide === "B" && (
                                                    <Chip
                                                        label="MASTER"
                                                        color="success"
                                                        size="small"
                                                        sx={{ ml: 1 }}
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {FIELD_META.map((meta) => {
                                            const chosenSide =
                                                fieldSides[meta.key] ?? masterSide;
                                            const aChosen = chosenSide === "A";
                                            const bChosen = chosenSide === "B";

                                            return (
                                                <TableRow
                                                    key={meta.key}
                                                    hover
                                                    onClick={() =>
                                                        toggleFieldSide(meta.key)
                                                    }
                                                    sx={{ cursor: "pointer" }}
                                                >
                                                    <TableCell
                                                        sx={{
                                                            fontWeight: "medium",
                                                            color: "text.secondary",
                                                            fontSize: "0.8rem",
                                                        }}
                                                    >
                                                        {meta.label}
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        sx={{
                                                            bgcolor: aChosen
                                                                ? "success.50"
                                                                : undefined,
                                                            border: aChosen
                                                                ? "2px solid"
                                                                : undefined,
                                                            borderColor: aChosen
                                                                ? "success.main"
                                                                : undefined,
                                                        }}
                                                    >
                                                        {aChosen && (
                                                            <CheckCircle
                                                                sx={{
                                                                    fontSize: 14,
                                                                    color: "success.main",
                                                                    mr: 0.5,
                                                                    verticalAlign: "middle",
                                                                }}
                                                            />
                                                        )}
                                                        {meta.render(productA!)}
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        sx={{
                                                            bgcolor: bChosen
                                                                ? "success.50"
                                                                : undefined,
                                                            border: bChosen
                                                                ? "2px solid"
                                                                : undefined,
                                                            borderColor: bChosen
                                                                ? "success.main"
                                                                : undefined,
                                                        }}
                                                    >
                                                        {bChosen && (
                                                            <CheckCircle
                                                                sx={{
                                                                    fontSize: 14,
                                                                    color: "success.main",
                                                                    mr: 0.5,
                                                                    verticalAlign: "middle",
                                                                }}
                                                            />
                                                        )}
                                                        {meta.render(productB!)}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TextField
                                fullWidth
                                label="Merge reason (optional)"
                                value={mergeReason}
                                onChange={(e) => setMergeReason(e.target.value)}
                                multiline
                                rows={2}
                                sx={{ mb: 2 }}
                                placeholder="e.g. Duplicate entry created accidentally"
                            />

                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <strong>
                                    This operation cannot be undone.
                                </strong>{" "}
                                All orders, purchases, stock, and images from{" "}
                                <strong>
                                    {duplicate?.name} (duplicate)
                                </strong>{" "}
                                will be merged into{" "}
                                <strong>{master?.name} (master)</strong>. The
                                duplicate product will be deactivated.
                            </Alert>

                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<MergeType />}
                                onClick={() => setConfirmOpen(true)}
                                disabled={merging}
                                size="large"
                            >
                                Merge Products
                            </Button>
                        </>
                    )}
                </Box>
            )}

            {/* ── Tab 1: Duplicate candidates ─────────────────────── */}
            {tab === 1 && (
                <Box>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Automatically detected products that may be
                            duplicates based on name similarity, category, brand,
                            and price.
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={handleLoadCandidates}
                            disabled={loadingCandidates}
                        >
                            {loadingCandidates ? "Scanning…" : "Scan for Duplicates"}
                        </Button>
                    </Box>

                    {loadingCandidates && <LinearProgress sx={{ mb: 2 }} />}
                    {candidateError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {candidateError}
                        </Alert>
                    )}

                    {candidates.length === 0 && !loadingCandidates && (
                        <Paper
                            variant="outlined"
                            sx={{ p: 4, textAlign: "center" }}
                        >
                            <Typography color="text.secondary">
                                Click "Scan for Duplicates" to detect potential
                                matches across your product catalogue.
                            </Typography>
                        </Paper>
                    )}

                    {candidates.length > 0 && (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Product 1</TableCell>
                                        <TableCell>Product 2</TableCell>
                                        <TableCell align="center">
                                            Match Score
                                        </TableCell>
                                        <TableCell align="center">
                                            Why Similar
                                        </TableCell>
                                        <TableCell align="center">
                                            Action
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {candidates.map((c, idx) => (
                                        <TableRow key={idx} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {c.product1_name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    SKU: {c.product1_sku} ·{" "}
                                                    {formatCurrencyBDT(c.product1_price)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {c.product2_name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    SKU: {c.product2_sku} ·{" "}
                                                    {formatCurrencyBDT(c.product2_price)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={`${Math.round(c.match_score * 100)}%`}
                                                    color={
                                                        c.match_score >= 0.85
                                                            ? "error"
                                                            : c.match_score >= 0.7
                                                              ? "warning"
                                                              : "default"
                                                    }
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        gap: 0.5,
                                                        flexWrap: "wrap",
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    {c.match_criteria
                                                        .same_category && (
                                                        <Chip
                                                            label="Same Category"
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                    {c.match_criteria
                                                        .same_brand && (
                                                        <Chip
                                                            label="Same Brand"
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                    {c.match_criteria
                                                        .sku_match && (
                                                        <Chip
                                                            label="Same SKU"
                                                            size="small"
                                                            color="error"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                    {(c.match_criteria
                                                        .name_similarity ?? 0) >
                                                        0.8 && (
                                                        <Chip
                                                            label="Similar Name"
                                                            size="small"
                                                            color="warning"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="warning"
                                                    startIcon={<Warning />}
                                                    onClick={() =>
                                                        handleReviewCandidate(c)
                                                    }
                                                >
                                                    Review & Merge
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            )}

            {/* ── Confirmation dialog ──────────────────────────────── */}
            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <MergeType color="error" />
                    Confirm Product Merge
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You are about to merge:
                    </DialogContentText>
                    <Box sx={{ my: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                        <Typography variant="body2">
                            <strong>Master (kept):</strong>{" "}
                            {master?.name}{" "}
                            <Chip
                                label={master?.sku}
                                size="small"
                                variant="outlined"
                                sx={{ ml: 0.5 }}
                            />
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Duplicate (deactivated):</strong>{" "}
                            {duplicate?.name}{" "}
                            <Chip
                                label={duplicate?.sku}
                                size="small"
                                variant="outlined"
                                sx={{ ml: 0.5 }}
                            />
                        </Typography>
                    </Box>
                    <DialogContentText color="error">
                        All orders, purchases, stock quantities, images, and
                        category tags from the duplicate will be transferred to
                        the master. The duplicate will be permanently deactivated.
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={merging}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleMerge}
                        disabled={merging}
                        startIcon={
                            merging ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <MergeType />
                            )
                        }
                    >
                        {merging ? "Merging…" : "Confirm Merge"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
